import { Fade, useTheme } from '@mui/material';
import type {
  EmblaCarouselType,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portfolioData } from '../Portfolio.data';
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from './EmblaCarouselArrowButtons';
import {
  EmblaContainer,
  EmblaSlide,
  EmblaSlideImg,
  Embla,
  EmblaViewport,
  EmblaWrapper,
  StyledModal,
  StyledImg,
  StyledImgWrapper,
} from './styles';
import { StaticImageData } from 'next/image';

const TWEEN_FACTOR_BASE = 0.22;

const PORTFOLIO_ITEMS = portfolioData.examples;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

type PropType = {
  slides: number[];
  options?: EmblaOptionsType;
};

export const EmblaCarousel = (props: PropType) => {
  const theme = useTheme();
  const isMobile = theme.utils.isMobile();
  const { slides, options } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    Autoplay({ stopOnInteraction: false }),
  ]);
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector('.embla__slide__item') as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === 'scroll';

      const distanceToScrollSnap = (
        slideIndex: number,
        scrollSnap: number
      ): number => {
        let diffToTarget = scrollSnap - scrollProgress;

        if (!engine.options.loop) return diffToTarget;

        engine.slideLooper.loopPoints.forEach((loopItem) => {
          const target = loopItem.target();
          if (slideIndex !== loopItem.index || target === 0) return;

          const sign = Math.sign(target);
          diffToTarget =
            sign === -1
              ? scrollSnap - (1 + scrollProgress)
              : scrollSnap + (1 - scrollProgress);
        });

        return diffToTarget;
      };

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          const diffToTarget = distanceToScrollSnap(slideIndex, scrollSnap);
          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const scale = numberWithinRange(tweenValue, 0, 1).toString();
          const tweenNode = tweenNodes.current[slideIndex];
          tweenNode.style.transform = `scale(${scale})`;
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenScale)
      .on('scroll', tweenScale)
      .on('slideFocus', tweenScale);
  }, [emblaApi, tweenScale, setTweenNodes, setTweenFactor]);

  const [open, setOpen] = useState(false);
  const [image, setImage] = useState('false');

  const handleClose = () => {
    setOpen(false);
    setIsZoomed(false);
  };

  const handleImage = (value: string) => {
    setImage(value);
    setOpen(true);
  };

  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });
  const [isZoomed, setIsZoomed] = useState(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCursorPosition({ x, y });
  };

  const handleZoom = () => {
    if (isMobile) {
      return;
    }
    setIsZoomed((prevValue) => !prevValue);
  };

  return (
    <>
      <StyledModal open={open} onClose={handleClose} closeAfterTransition>
        <Fade in={open} timeout={500}>
          <StyledImgWrapper
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleClose();
            }}>
            <StyledImg
              src={(image as unknown as StaticImageData)?.src}
              onMouseMove={handleMouseMove}
              onClick={(e) => {
                e.stopPropagation();
                handleZoom();
              }}
              cursorX={cursorPosition.x}
              cursorY={cursorPosition.y}
              isZoomed={isZoomed}
              style={{
                transform: isZoomed ? 'scale(2)' : 'scale(1)',
              }}
            />
          </StyledImgWrapper>
        </Fade>
      </StyledModal>
      <EmblaWrapper>
        <PrevButton
          onClick={onPrevButtonClick}
          disabled={prevBtnDisabled}
          aria-label="previous-image"
        />
        <Embla>
          <EmblaViewport ref={emblaRef}>
            <EmblaContainer>
              {slides.map((index) => (
                <EmblaSlide key={index}>
                  <EmblaSlideImg
                    className="embla__slide__item"
                    src={
                      (
                        PORTFOLIO_ITEMS[index]
                          ?.img as unknown as StaticImageData
                      )?.src
                    }
                    onClick={() => {
                      handleImage(PORTFOLIO_ITEMS[index]?.img);
                    }}
                    alt={PORTFOLIO_ITEMS[index]?.alt}></EmblaSlideImg>
                </EmblaSlide>
              ))}
            </EmblaContainer>
          </EmblaViewport>
        </Embla>
        <NextButton
          onClick={onNextButtonClick}
          disabled={nextBtnDisabled}
          aria-label="next-image"
        />
      </EmblaWrapper>
    </>
  );
};
